// The scout run AS a trace.
//
// A Discover run is an agent session — turns, tool calls, results — so it can
// open in the trace sidebar with every instrument the product already points
// at traces. At run time the sub-agent's pi-agent-core message history is
// synthesized into an Anthropic-shaped request body (the exact input
// parseApiRequest eats) and stored beside the report; at read time a
// synthetic trace id resolves those parts locally instead of hitting the
// analytics graph. Metadata a real capture would carry is defaulted
// sensibly: the dashboard itself is the endpoint, the report's profile is
// the summary, the scout's own run label stands in for a cluster name, and
// the report's themes stand in for session-cluster activity.

import { readDiscovery } from "./anatomyDiscoveryStore.js";

const ID_PREFIX = "discover:";

export function discoverTraceId(scopeKey) {
    return `${ID_PREFIX}${scopeKey || "org"}`;
}

export function isDiscoverTraceId(id) {
    return String(id || "").startsWith(ID_PREFIX);
}

// Body caps: a stored trace body must fit localStorage alongside the report.
// Tool results dominate (row JSON); everything else is prose-sized — except
// return_result, whose input IS the report and must survive whole.
const TEXT_CAP = 4000;
const RESULT_CAP = 6000;
const INPUT_CAP = 32_000;
const BODY_BYTE_CAP = 400_000;
const RESULT_CAP_TIGHT = 1200;
const INPUT_CAP_TIGHT = 8000;

function capText(v, cap) {
    const t = String(v ?? "");
    return t.length > cap ? `${t.slice(0, cap - 1)}…` : t;
}

function contentTextOf(m) {
    if (typeof m?.content === "string") return m.content;
    return (Array.isArray(m?.content) ? m.content : [])
        .filter((c) => c?.type === "text")
        .map((c) => c.text)
        .join("");
}

// Oversized inputs keep a capped JSON preview — a bare "truncated" stub
// throws away the call's substance (return_result's input is the report).
function toolUseInput(args, cap) {
    const input = args ?? {};
    let s;
    try {
        s = JSON.stringify(input);
    } catch {
        return { truncated: true };
    }
    if (s.length <= cap) return input;
    return { truncated: true, preview: `${s.slice(0, cap - 1)}…` };
}

function messagesToAnthropic(messages, resultCap, inputCap) {
    const out = [];
    for (const m of messages || []) {
        if (m?.role === "user") {
            const text = contentTextOf(m).trim();
            if (text) out.push({ role: "user", content: [{ type: "text", text: capText(text, TEXT_CAP) }] });
        } else if (m?.role === "assistant") {
            const blocks = [];
            for (const c of Array.isArray(m.content) ? m.content : []) {
                if (c?.type === "thinking" && c.text?.trim()) {
                    blocks.push({ type: "thinking", thinking: capText(c.text, TEXT_CAP) });
                } else if (c?.type === "text" && c.text?.trim()) {
                    blocks.push({ type: "text", text: capText(c.text, TEXT_CAP) });
                } else if (c?.type === "toolCall" && c.name) {
                    blocks.push({
                        type: "tool_use",
                        id: c.id || `tu_${out.length}_${blocks.length}`,
                        name: c.name,
                        input: toolUseInput(c.arguments ?? c.input ?? c.args, inputCap),
                    });
                }
            }
            if (blocks.length) out.push({ role: "assistant", content: blocks });
        } else if (m?.role === "toolResult") {
            out.push({
                role: "user",
                content: [{
                    type: "tool_result",
                    tool_use_id: m.toolCallId || "",
                    is_error: Boolean(m.isError),
                    content: [{ type: "text", text: capText(contentTextOf(m), resultCap) }],
                }],
            });
        }
    }
    return out;
}

// The run's real tool definitions, declared the way a captured request
// declares its offered tools — this is what feeds the panel's tools pill
// and tool-defs viewer.
function toolsToAnthropic(tools) {
    return (Array.isArray(tools) ? tools : [])
        .filter((t) => t?.name)
        .map((t) => {
            let schema = {};
            try {
                schema = t.parameters ? JSON.parse(JSON.stringify(t.parameters)) : {};
            } catch { /* a non-serializable schema declares as empty */ }
            return { name: t.name, description: capText(t.description || "", 2000), input_schema: schema };
        });
}

// pi-agent-core history → an Anthropic-shaped request body string. Two-pass
// budget: if the full body exceeds the byte cap, tool results re-cap tightly —
// they carry the bulk, and the dock already renders their preview tables.
export function buildDiscoverTraceBody({ messages, model, system, tools }) {
    const declaredTools = toolsToAnthropic(tools);
    const assemble = (resultCap, inputCap) => JSON.stringify({
        model: model || "unknown-model",
        max_tokens: 0,
        stream: false,
        system: capText(system || "", 8000),
        tools: declaredTools,
        messages: messagesToAnthropic(messages, resultCap, inputCap),
    });
    let body = assemble(RESULT_CAP, INPUT_CAP);
    if (body.length > BODY_BYTE_CAP) body = assemble(RESULT_CAP_TIGHT, INPUT_CAP_TIGHT);
    return body;
}

// Everything the getTrace seam needs to adapt a synthetic id: the stored
// body plus defaulted metadata. null when the scope has no stored run (or a
// pre-capture entry) — the panel then renders its normal not-found state.
export function discoverTraceParts(id) {
    if (!isDiscoverTraceId(id)) return null;
    const scopeKey = String(id).slice(ID_PREFIX.length) || "org";
    const entry = readDiscovery().reports?.[scopeKey];
    if (!entry?.traceBody) return null;
    const meta = entry.meta || {};
    const report = entry.report || {};
    return {
        apiRequestRaw: entry.traceBody,
        focal: {
            trace_id: id,
            endpoint_id: "",
            endpoint_name: "this dashboard",
            session_id: id,
            event_time: Number.isFinite(meta.at) ? new Date(meta.at).toISOString() : "",
            provider: "anthropic",
            model: meta.model || "",
            summary: report.profile || "",
            // The scout names its own run — that label stands in for the
            // cluster a captured trace would carry. The large sentinel id
            // satisfies the named-cluster chip path (which needs id > 0)
            // without colliding with real low-numbered clusters.
            cluster_id: report.runLabel || report.profile ? 999999901 : 0,
            cluster_name: report.runLabel || `Scout discovery — ${meta.scopeText || "this scope"}`,
            // The report's themes stand in for session-cluster activity.
            session_clusters: (report.themes || []).map((t, i) => ({
                id: 999999901 + i,
                name: t.name,
                traceCount: Number.isFinite(t.events) ? t.events : 0,
            })),
        },
    };
}
